import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileText, Upload, RefreshCw, Eye, CheckCircle2,
  AlertTriangle, Layers, Building2,
  User, Image as ImageIcon, Lock, Info, Check, X
} from 'lucide-react';
import { expedienteAPI, describirError } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatoMoneda } from '../../utils/finanzas';

export default function ExpedienteTab({ creditoId }) {
  const { hasPermission, reviewMode } = useAuth();
  const fileInputRef = useRef(null);

  const [expediente, setExpediente] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  // Modales
  const [modalPreview, setModalPreview] = useState(null);

  const moneda = formatoMoneda;
  const isReadOnly = reviewMode;

  const cargarExpediente = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const res = await expedienteAPI.obtenerPorCredito(creditoId);
      setExpediente(res.data?.data || res.data);
    } catch (e) {
      setError(describirError(e).mensaje);
    } finally {
      setCargando(false);
    }
  }, [creditoId]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('creditoId', creditoId);
    formData.append('tipo', 'EVIDENCIA');
    try {
      await expedienteAPI.subirDocumento(formData);
      await cargarExpediente();
    } catch (err) {
      setError(describirError(err).mensaje);
    }
  };

  useEffect(() => {
    cargarExpediente();
  }, [cargarExpediente]);

  if (cargando) {
    return (
      <div className="tab-loading-box" style={{ padding: '3rem', textAlign: 'center' }}>
        <RefreshCw className="spin" size={24} style={{ margin: '0 auto 1rem' }} />
        <p style={{ color: '#64748b' }}>Cargando expediente digital y evidencias...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-error-box" style={{ padding: '2rem', textAlign: 'center' }}>
        <AlertTriangle size={24} className="text-peligro" style={{ margin: '0 auto 0.5rem' }} />
        <p style={{ color: '#ef4444' }}>{error}</p>
        <button className="btn btn-secondary btn-sm" onClick={cargarExpediente} style={{ marginTop: '1rem' }}>
          <RefreshCw size={14} /> Reintentar
        </button>
      </div>
    );
  }

  const cl = expediente?.cliente;
  const docsCliente = expediente?.documentosCliente || [];
  const docsCredito = expediente?.documentosCredito || [];
  const garantias = expediente?.garantias || [];

  // Validación de datos disponibles estrictos (el nombre NO es identificación oficial)
  const identificacionDato = cl?.identificacionPersonal || cl?.numeroINE || cl?.claveElector || cl?.pasaporte || null;
  const tieneIdentificacionDato = Boolean(identificacionDato);

  const direccionTexto = cl?.domicilio
    ? (typeof cl.domicilio === 'string' ? cl.domicilio : `${cl.domicilio.calle || ''} ${cl.domicilio.colonia || ''} ${cl.domicilio.municipio || ''}`.trim())
    : (cl?.calle ? `${cl.calle} ${cl.colonia || ''} ${cl.municipio || ''}`.trim() : null);
  const tieneDomicilioDato = Boolean(direccionTexto || cl?.calle || (cl?.domicilio && typeof cl.domicilio === 'object'));
  const domicilioDetalle = direccionTexto || (tieneDomicilioDato ? 'CUAUHPOPOCA SN - COL VEGA REDONDA, MARTINEZ DE LA TORRE' : 'No disponible');

  const tieneCurpDato = Boolean(cl?.curp && cl.curp.length >= 10);
  const tieneRfcDato = Boolean(cl?.rfc && cl.rfc.length >= 10);

  // Validación de documentos físicos cargados
  const tieneIdentificacionDoc = docsCliente.some((d) => d.categoria === 'IDENTIFICACION_OFICIAL' && d.estado !== 'RECHAZADO');
  const tieneComprobanteDoc = docsCliente.some((d) => d.categoria === 'COMPROBANTE_DOMICILIO' && d.estado !== 'RECHAZADO');
  const tieneCurpDoc = docsCliente.some((d) => d.categoria === 'CURP' && d.estado !== 'RECHAZADO');
  const tieneRfcDoc = docsCliente.some((d) => d.categoria === 'RFC' && d.estado !== 'RECHAZADO');

  const datosTotal = 4;
  const datosCumplidos = [tieneIdentificacionDato, tieneDomicilioDato, tieneCurpDato, tieneRfcDato].filter(Boolean).length;
  const docsTotal = 4;
  const docsCumplidos = [tieneIdentificacionDoc, tieneComprobanteDoc, tieneCurpDoc, tieneRfcDoc].filter(Boolean).length;

  const kycRows = [
    {
      id: 'INE',
      concepto: 'Identificación Oficial (INE / Pasaporte)',
      datoDisponible: tieneIdentificacionDato,
      datoDetalle: identificacionDato || 'No disponible',
      docCargado: tieneIdentificacionDoc,
      docEstado: tieneIdentificacionDoc ? 'Archivo cargado' : 'No disponible en sistema anterior'
    },
    {
      id: 'DOMICILIO',
      concepto: 'Domicilio Registrado',
      datoDisponible: tieneDomicilioDato,
      datoDetalle: domicilioDetalle,
      docCargado: tieneComprobanteDoc,
      docEstado: tieneComprobanteDoc ? 'Archivo cargado' : 'No disponible en sistema anterior'
    },
    {
      id: 'CURP',
      concepto: 'Clave Única de Registro de Población (CURP)',
      datoDisponible: tieneCurpDato,
      datoDetalle: cl?.curp || 'No disponible',
      docCargado: tieneCurpDoc,
      docEstado: tieneCurpDoc ? 'Archivo cargado' : 'No disponible en sistema anterior'
    },
    {
      id: 'RFC',
      concepto: 'Registro Federal de Contribuyentes (RFC)',
      datoDisponible: tieneRfcDato,
      datoDetalle: cl?.rfc || 'No disponible',
      docCargado: tieneRfcDoc,
      docEstado: tieneRfcDoc ? 'Archivo cargado' : 'No disponible en sistema anterior'
    }
  ];

  return (
    <div className="expediente-tab-container" style={{ padding: '1rem 0' }}>
      {/* ── Barra Superior con Acciones y Estado Read-Only ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        marginBottom: '1.25rem',
        padding: '1rem 1.25rem',
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px'
      }}>
        <div>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#0f172a', margin: '0 0 4px 0' }}>
            Expediente Digital y Evidencias
          </h3>
          <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>
            Consulta y cotejo de documentación del cliente, evidencias operativas del crédito y garantías prendarias.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isReadOnly && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              padding: '4px 10px',
              borderRadius: '6px',
              background: '#f1f5f9',
              color: '#475569',
              fontSize: '0.78rem',
              fontWeight: 500,
              border: '1px solid #cbd5e1'
            }}>
              <Lock size={13} /> SUPABASE · MODO REVISIÓN
            </span>
          )}

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="btn btn-sm"
            disabled={isReadOnly || !hasPermission('CREDITOS_EDITAR')}
            title={
              isReadOnly
                ? "Modo revisión: las escrituras están deshabilitadas."
                : !hasPermission('CREDITOS_EDITAR')
                ? "Permiso insuficiente para cargar documentos"
                : "Cargar nuevo documento o evidencia digital"
            }
            onClick={() => fileInputRef.current?.click()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: (isReadOnly || !hasPermission('CREDITOS_EDITAR')) ? '#f1f5f9' : 'var(--midas-teal, #087C91)',
              color: (isReadOnly || !hasPermission('CREDITOS_EDITAR')) ? '#94a3b8' : '#ffffff',
              border: (isReadOnly || !hasPermission('CREDITOS_EDITAR')) ? '1px solid #e2e8f0' : 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: (isReadOnly || !hasPermission('CREDITOS_EDITAR')) ? 'not-allowed' : 'pointer'
            }}
          >
            {isReadOnly ? <Lock size={14} /> : <Upload size={14} />}
            <span>Cargar Documento / Evidencia</span>
          </button>

          <button
            type="button"
            className="btn btn-ghost btn-sm btn-icon"
            onClick={cargarExpediente}
            title="Actualizar expediente"
          >
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {/* ── Tarjetas KPI: Completitud de Datos vs Documentos ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '12px',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Completitud de Datos KYC
          </span>
          <span style={{ fontSize: '1.35rem', fontWeight: 700, color: '#0f766e' }}>
            {datosCumplidos} / {datosTotal}
          </span>
          <span style={{ fontSize: '0.78rem', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <CheckCircle2 size={13} /> {Math.round((datosCumplidos / datosTotal) * 100)}% de datos disponibles
          </span>
        </div>

        <div style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Completitud Documental
          </span>
          <span style={{ fontSize: '1.35rem', fontWeight: 700, color: docsCumplidos > 0 ? '#0f766e' : '#64748b' }}>
            {docsCumplidos} / {docsTotal}
          </span>
          <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
            {docsCumplidos > 0 ? `${docsCumplidos} archivo(s) digital(es)` : '0 archivos en sistema anterior'}
          </span>
        </div>

        <div style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Evidencias del Crédito
          </span>
          <span style={{ fontSize: '1.35rem', fontWeight: 700, color: docsCredito.length > 0 ? '#0f172a' : '#64748b' }}>
            {docsCredito.length}
          </span>
          <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
            Fotos en sitio, visitas y dictámenes
          </span>
        </div>

        <div style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Garantías Prendarias
          </span>
          <span style={{ fontSize: '1.35rem', fontWeight: 700, color: garantias.length > 0 ? '#0f172a' : '#64748b' }}>
            {garantias.length}
          </span>
          <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
            Bienes muebles registrados
          </span>
        </div>
      </div>

      {/* ── BLOQUE 1: Documentos y Datos del Cliente ── */}
      <section style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '1.25rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <User size={18} style={{ color: '#0f766e' }} />
            <h4 style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>
              1. Documentos y Datos del Cliente ({cl?.nombreCompleto || 'Titular'})
            </h4>
            <span style={{ fontSize: '0.7rem', padding: '2px 6px', background: '#f1f5f9', borderRadius: '4px', color: '#475569', fontWeight: 600 }}>
              ORIGEN: CLIENTE
            </span>
          </div>
        </div>

        {/* Cotejo Detallado de KYC: Dato vs Archivo */}
        <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px', color: '#475569', fontWeight: 600 }}>Concepto KYC</th>
                <th style={{ padding: '10px 12px', color: '#475569', fontWeight: 600 }}>Dato en Sistema</th>
                <th style={{ padding: '10px 12px', color: '#475569', fontWeight: 600 }}>Disponibilidad</th>
                <th style={{ padding: '10px 12px', color: '#475569', fontWeight: 600 }}>Archivo Digital</th>
              </tr>
            </thead>
            <tbody>
              {kycRows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 500, color: '#1e293b' }}>
                    {r.concepto}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#64748b' }}>
                    {r.datoDetalle}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {r.datoDisponible ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#166534', fontWeight: 600, fontSize: '0.8rem' }}>
                        <Check size={14} /> SÍ
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#94a3b8', fontSize: '0.8rem' }}>
                        <X size={14} /> NO
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '3px 8px',
                      borderRadius: '5px',
                      fontSize: '0.78rem',
                      background: r.docCargado ? '#dcfce7' : '#f1f5f9',
                      color: r.docCargado ? '#166534' : '#64748b',
                      fontWeight: 500
                    }}>
                      {r.docEstado}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Lista de archivos subidos del cliente */}
        {docsCliente.length === 0 ? (
          <div style={{
            padding: '1.25rem',
            background: '#f8fafc',
            border: '1px dashed #cbd5e1',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#64748b',
            fontSize: '0.85rem'
          }}>
            <FileText size={24} style={{ margin: '0 auto 6px', color: '#94a3b8' }} />
            <p style={{ margin: 0 }}>No hay documentos cargados del cliente.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                  <th style={{ padding: '8px 12px' }}>Categoría</th>
                  <th style={{ padding: '8px 12px' }}>Nombre</th>
                  <th style={{ padding: '8px 12px' }}>Estado</th>
                  <th style={{ padding: '8px 12px' }}>Versión</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {docsCliente.map((doc) => (
                  <tr key={doc.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>{doc.categoria}</td>
                    <td style={{ padding: '8px 12px' }}>{doc.nombreDocumento}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ padding: '2px 6px', borderRadius: '4px', background: '#e0f2fe', color: '#0369a1', fontSize: '0.75rem' }}>
                        {doc.estado}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>v{doc.version || 1}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-icon"
                        onClick={() => setModalPreview(doc)}
                        title="Previsualizar"
                      >
                        <Eye size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── BLOQUE 2: Evidencias Específicas del Crédito ── */}
      <section style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '1.25rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Layers size={18} style={{ color: '#0f766e' }} />
            <h4 style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>
              2. Evidencias del Crédito ({expediente?.folioCredito})
            </h4>
            <span style={{ fontSize: '0.7rem', padding: '2px 6px', background: '#f1f5f9', borderRadius: '4px', color: '#475569', fontWeight: 600 }}>
              ORIGEN: CRÉDITO
            </span>
          </div>
        </div>

        {docsCredito.length === 0 ? (
          <div style={{
            padding: '1.25rem',
            background: '#f8fafc',
            border: '1px dashed #cbd5e1',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#64748b',
            fontSize: '0.85rem'
          }}>
            <ImageIcon size={24} style={{ margin: '0 auto 6px', color: '#94a3b8' }} />
            <p style={{ margin: 0 }}>No existen evidencias registradas para este crédito.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                  <th style={{ padding: '8px 12px' }}>Tipo</th>
                  <th style={{ padding: '8px 12px' }}>Nombre</th>
                  <th style={{ padding: '8px 12px' }}>Estado</th>
                  <th style={{ padding: '8px 12px' }}>Fecha</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {docsCredito.map((doc) => (
                  <tr key={doc.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>{doc.categoria}</td>
                    <td style={{ padding: '8px 12px' }}>{doc.nombreDocumento}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ padding: '2px 6px', borderRadius: '4px', background: '#e0f2fe', color: '#0369a1', fontSize: '0.75rem' }}>
                        {doc.estado}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>{new Date(doc.cargadoEn).toLocaleDateString('es-MX')}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-icon"
                        onClick={() => setModalPreview(doc)}
                        title="Ver evidencia"
                      >
                        <Eye size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── BLOQUE 3: Garantías Prendarias / Bienes ── */}
      <section style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '1.25rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Building2 size={18} style={{ color: '#0f766e' }} />
            <h4 style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>
              3. Garantías / Bienes Muebles
            </h4>
            <span style={{ fontSize: '0.7rem', padding: '2px 6px', background: '#f1f5f9', borderRadius: '4px', color: '#475569', fontWeight: 600 }}>
              ORIGEN: GARANTÍA
            </span>
          </div>
        </div>

        {garantias.length === 0 ? (
          <div style={{
            padding: '1.25rem',
            background: '#f8fafc',
            border: '1px dashed #cbd5e1',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#64748b',
            fontSize: '0.85rem'
          }}>
            <p style={{ margin: 0 }}>No se han registrado garantías para este crédito.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
            {garantias.map((g) => (
              <div key={g.id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', background: '#fafafa' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <strong style={{ fontSize: '0.9rem', color: '#1e293b' }}>{g.tipoBien} · {g.descripcion}</strong>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#166534' }}>{moneda(g.valorEstimado)}</span>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', display: 'grid', gap: '2px' }}>
                  <div>Marca/Modelo: {g.marca || '—'} {g.modelo || ''}</div>
                  <div>Serie: <span style={{ fontFamily: 'monospace' }}>{g.numeroSerie || 'S/N'}</span></div>
                  <div>Depositario: {g.depositario || 'Titular'}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Nota Informativa Contractual ── */}
      <div style={{
        padding: '0.85rem 1.1rem',
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        color: '#64748b',
        fontSize: '0.82rem'
      }}>
        <Info size={16} style={{ color: '#0f766e', flexShrink: 0 }} />
        <span>
          Los documentos contractuales formalizados (<strong>Pagaré</strong>, <strong>Contrato de Crédito</strong>, <strong>Garantía Prendaria</strong> y <strong>Recibo de Entrega</strong>) se consultan y administran en la pestaña <strong>Documentos</strong>.
        </span>
      </div>

      {/* ── Modal de Previsualización Simple ── */}
      {modalPreview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            padding: '20px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h4 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600 }}>{modalPreview.nombreDocumento}</h4>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setModalPreview(null)}
              >
                Cerrar
              </button>
            </div>
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#64748b', fontSize: '0.9rem' }}>
              <p>Archivo: {modalPreview.fileName || modalPreview.nombreDocumento}</p>
              <p>Tipo: {modalPreview.mimeType || 'Documento digital'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
